/**
 * Tiny markdown-to-html renderer for the KI chat bubbles — the agent answers with
 * bold, lists, inline code and (despite instructions) the occasional TABLE; raw pipe
 * rows were unreadable. Input is escaped first, so [innerHTML] stays safe.
 */
export function renderChatMarkdown(text: string): string {
    const escaped = (text ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const lines = escaped.split('\n');
    const html: string[] = [];
    let table: string[][] | null = null;

    const flushTable = (): void => {
        if (!table) {
            return;
        }

        const [head, ...rows] = table;
        html.push(
            '<table class="chat-md-table"><thead><tr>'
            + head.map((cell) => `<th>${cell}</th>`).join('')
            + '</tr></thead><tbody>'
            + rows.map((row) => '<tr>' + row.map((cell) => `<td>${cell}</td>`).join('') + '</tr>').join('')
            + '</tbody></table>');
        table = null;
    };

    for (const line of lines) {
        const trimmed = line.trim();

        // Table rows: | a | b | — the |---|---| separator line is skipped
        if (/^\|(.+)\|$/.test(trimmed)) {
            if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
                continue;
            }

            const cells = trimmed.slice(1, -1).split('|').map((cell) => inline(cell.trim()));
            (table ??= []).push(cells);
            continue;
        }

        flushTable();

        if (/^[-*] /.test(trimmed)) {
            html.push(`<div class="pl-3">• ${inline(trimmed.slice(2))}</div>`);
        } else if (/^#{1,4} /.test(trimmed)) {
            html.push(`<div class="font-semibold">${inline(trimmed.replace(/^#{1,4} /, ''))}</div>`);
        } else if (trimmed === '---' || trimmed === '***') {
            html.push('<hr class="my-1 border-black/10">');
        } else if (trimmed.length === 0) {
            html.push('<div class="h-2"></div>');
        } else {
            html.push(`<div>${inline(line)}</div>`);
        }
    }

    flushTable();

    return html.join('');
}

/** Inline marks: bold, italic, inline code. */
function inline(text: string): string {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
        .replace(/`([^`]+)`/g, '<code class="rounded bg-black/[.06] px-1">$1</code>')
        .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/g, '$1<i>$2</i>');
}
